// src/components/admin/AdminSettingsSection.jsx
import React, { useEffect, useState } from "react";
import { Settings, RotateCw, Activity, Eye, EyeOff } from "lucide-react";
import axiosInstance from "../../utils/axiosInstance";
import toast from "react-hot-toast";

const AdminSettingsSection = ({
  ntsCreds,
  handleNtsChange,
  saveNtsCreds,
  ntsList,
  ntsEdit,
  handleNtsEditChange,
  updateNtsCreds,
}) => {
  const [refreshTaskId, setRefreshTaskId] = useState(null);
  const [refreshState, setRefreshState] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);

  const [showPassword, setShowPassword] = useState(false);

  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleRefreshClick = async () => {
    setRefreshError(null);
    const loadingToast = toast.loading(
      "در حال قرار دادن بروزرسانی سامانه در صف..."
    );

    try {
      const res = await axiosInstance.post("/ntsw/refresh-cookies/");
      const id = res.data.task_id;

      if (!id) {
        setIsRefreshing(false);
        toast.error("شناسه تسک از سرور دریافت نشد.", { id: loadingToast });
        return;
      }

      setRefreshTaskId(id);
      setRefreshState(res.data.status || "PENDING");
      setIsRefreshing(true);

      toast.success("بروزرسانی سامانه در صف پردازش قرار گرفت.", {
        id: loadingToast,
      });
    } catch (err) {
      setIsRefreshing(false);
      toast.error("ثبت بروزرسانی سامانه ناموفق بود.", { id: loadingToast });
    }
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    const loadingToast = toast.loading("در حال تست اتصال به سامانه جامع...");

    try {
      const res = await axiosInstance.get("/ntsw/validate-cookies/");
      const data = res.data || {};

      if (data.ok && data.valid) {
        const result = {
          valid: true,
          timestamp: new Date().toISOString(),
        };
        setTestResult(result);
        toast.success("اتصال به سامانه جامع برقرار است.", {
          id: loadingToast,
        });
      } else {
        const msg =
          data.reason ||
          data.error ||
          "کوکی‌های سامانه معتبر نیستند یا دسترسی مشکل دارد.";
        const result = {
          valid: false,
          error: msg,
          timestamp: new Date().toISOString(),
        };
        setTestResult(result);
        toast.error(msg, { id: loadingToast });
      }
    } catch (err) {
      const msg = "تست اتصال ناموفق بود.";
      setTestResult({
        valid: false,
        error: msg,
        timestamp: new Date().toISOString(),
      });
      toast.error(msg, { id: loadingToast });
    } finally {
      setIsTesting(false);
    }
  };

  useEffect(() => {
    if (!refreshTaskId) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const res = await axiosInstance.get(
          `/ntsw/refresh-cookies/status/${refreshTaskId}/`
        );
        if (cancelled) return;

        const state = res.data.state;
        setRefreshState(state);

        if (state === "SUCCESS") {
          setIsRefreshing(false);
          toast.success("بروزرسانی کوکی‌های سامانه جامع با موفقیت انجام شد.");
          return;
        }

        if (state === "FAILURE" || state === "REVOKED") {
          setIsRefreshing(false);
          const errMsg =
            res.data.error ||
            (typeof res.data.result === "string"
              ? res.data.result
              : "خطا در بروزرسانی سامانه");
          setRefreshError(errMsg);
          toast.error(errMsg);
          return;
        }
      } catch (err) {
        if (!cancelled) {
          setIsRefreshing(false);
          setRefreshError("خطا در دریافت وضعیت بروزرسانی سامانه");
          toast.error("خطا در دریافت وضعیت بروزرسانی سامانه");
          return;
        }
      }

      if (!cancelled) {
        setTimeout(poll, 3000);
      }
    };

    poll();

    return () => {
      cancelled = true;
    };
  }, [refreshTaskId]);

  const formatStateLabel = (state) => {
    if (!state) return "در انتظار شروع...";
    switch (state) {
      case "PENDING":
        return "در صف پردازش";
      case "STARTED":
        return "در حال اجرا";
      case "SUCCESS":
        return "با موفقیت انجام شد";
      case "FAILURE":
        return "خطا در اجرا";
      case "REVOKED":
        return "لغو شده";
      default:
        return state;
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" dir="rtl">
      {/* کارت اصلی تنظیمات دسترسی */}
      <div className="bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-slate-200/80 p-6 sm:p-7 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-500 flex items-center justify-center text-white shadow-sm">
            <Settings size={20} />
          </div>
          <div className="flex flex-col items-end">
            <h2 className="text-base sm:text-lg font-bold text-slate-900">
              دسترسی سامانه جامع
            </h2>
            <p className="text-[11px] sm:text-xs text-slate-500">
              ذخیره، بروزرسانی و تست اتصال سامانه جامع تجارت
            </p>
          </div>
        </div>

        <div className="space-y-4 text-xs sm:text-sm">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              نام کاربری سامانه جامع
            </label>
            <input
              type="text"
              name="username"
              value={ntsCreds.username}
              onChange={handleNtsChange}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-xs sm:text-sm text-slate-900 shadow-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500"
              placeholder="نام کاربری را وارد کنید"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              رمز عبور سامانه جامع
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={ntsCreds.password}
                onChange={handleNtsChange}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 pr-3.5 pl-10 py-2.5 text-xs sm:text-sm text-slate-900 shadow-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500"
                placeholder="رمز عبور را وارد کنید"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400 hover:text-slate-600 focus:outline-none"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 sm:gap-3 justify-end pt-2">
          <button
            onClick={saveNtsCreds}
            className="px-4 sm:px-5 py-2.5 bg-gradient-to-l from-sky-500 via-indigo-500 to-blue-600 text-white text-xs sm:text-sm font-semibold rounded-xl shadow-md hover:from-sky-600 hover:via-indigo-600 hover:to-blue-700 transition"
          >
            ذخیره دسترسی سامانه جامع
          </button>

          <button
            onClick={handleRefreshClick}
            disabled={isRefreshing}
            className={`px-4 sm:px-5 py-2.5 text-white text-xs sm:text-sm font-semibold rounded-xl shadow-md inline-flex items-center gap-2 transition ${
              isRefreshing
                ? "bg-emerald-500 cursor-not-allowed"
                : "bg-emerald-600 hover:bg-emerald-700"
            }`}
          >
            <RotateCw
              size={18}
              className={isRefreshing ? "animate-spin" : ""}
            />
            {isRefreshing ? "در حال بروزرسانی..." : "بروزرسانی سامانه"}
          </button>

          <button
            onClick={handleTestConnection}
            disabled={isTesting}
            className={`px-4 sm:px-5 py-2.5 text-white text-xs sm:text-sm font-semibold rounded-xl shadow-md inline-flex items-center gap-2 transition ${
              isTesting
                ? "bg-indigo-400 cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700"
            }`}
          >
            <Activity size={18} className={isTesting ? "animate-spin" : ""} />
            {isTesting ? "در حال تست اتصال..." : "تست اتصال سامانه"}
          </button>
        </div>

        {/* وضعیت تسک بروزرسانی کوکی‌ها */}
        {refreshTaskId && (
          <div className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 mt-3 space-y-2 text-xs">
            <div className="flex items-center gap-2">
              <Activity className="text-emerald-600" size={18} />
              <h3 className="text-sm font-semibold text-slate-800">
                وضعیت بروزرسانی سامانه جامع
              </h3>
            </div>
            <div className="flex items-center justify-between gap-2 text-slate-700">
              <span className="text-slate-600">شناسه تسک:</span>
              <code className="bg-white border border-slate-200 rounded px-2 py-1 text-[11px] truncate">
                {refreshTaskId}
              </code>
            </div>
            <div className="flex items-center justify-between gap-2 text-slate-700">
              <span className="text-slate-600">وضعیت:</span>
              <span
                className={`px-2 py-1 rounded-full text-[11px] font-semibold ${
                  refreshState === "SUCCESS"
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : refreshState === "FAILURE" || refreshState === "REVOKED"
                    ? "bg-rose-50 text-rose-700 border border-rose-200"
                    : "bg-amber-50 text-amber-700 border border-amber-200"
                }`}
              >
                {formatStateLabel(refreshState)}
              </span>
            </div>
            {refreshError && (
              <p className="mt-1 text-[11px] text-rose-600 leading-relaxed">
                {refreshError}
              </p>
            )}
          </div>
        )}

        {/* نتیجه آخرین تست اتصال */}
        {testResult && (
          <div className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 mt-3 space-y-2 text-xs">
            <div className="flex items-center gap-2">
              <Activity
                className={
                  testResult.valid ? "text-emerald-600" : "text-rose-600"
                }
                size={18}
              />
              <h3 className="text-sm font-semibold text-slate-800">
                نتیجه آخرین تست اتصال
              </h3>
            </div>
            <p className="text-slate-700">
              وضعیت:{" "}
              <span
                className={`px-2 py-1 rounded-full text-[11px] font-semibold ${
                  testResult.valid
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-rose-50 text-rose-700 border border-rose-200"
                }`}
              >
                {testResult.valid
                  ? "اتصال برقرار است"
                  : "اتصال یا کوکی‌ها نامعتبر است"}
              </span>
            </p>
            {testResult.error && (
              <p className="text-[11px] text-rose-600 mt-1">
                {testResult.error}
              </p>
            )}
            {testResult.timestamp && (
              <p className="text-[11px] text-slate-500 mt-1">
                زمان تست: {testResult.timestamp}
              </p>
            )}
          </div>
        )}

        <div className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 mt-3">
          <h3 className="text-sm font-semibold text-slate-800 mb-2">
            آخرین دسترسی‌های ثبت‌شده
          </h3>
          {ntsList.length === 0 ? (
            <p className="text-[11px] sm:text-xs text-slate-500">
              هنوز موردی ثبت نشده است.
            </p>
          ) : (
            <ul className="space-y-2 text-xs text-slate-700">
              {ntsList.map((item, idx) => (
                <li
                  key={`${item.username}-${idx}`}
                  className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2"
                >
                  <span className="font-medium text-slate-900">
                    {item.username}
                  </span>
                  <span className="text-slate-500 text-[11px]">
                    {item.created_at ? item.created_at : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSettingsSection;
